import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT } from "../../lib/siteData";

const links = [
  { label: "Work", href: "#work" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Social", href: "#social" },
  { label: "Review", href: "#reviews" },
  { label: "Estimate", href: "#estimate" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-[#223026]" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-2.5 group shrink-0">
          <span className="w-2.5 h-2.5 bg-lime-400 group-hover:rotate-45 transition-transform duration-300" />
          <span className="font-display font-extrabold text-[12px] sm:text-[13px] lg:text-sm uppercase tracking-[0.08em] text-white leading-none whitespace-nowrap">
            Eklund Land Management<span className="text-lime-400">.</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm text-neutral-400 hover:text-white tracking-wide transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </div>

        <a
          href={CONTACT.phoneHref}
          data-testid="nav-call-btn"
          className="hidden lg:inline-flex items-center gap-2 bg-lime-400 text-black text-sm font-semibold px-5 py-2.5 hover:bg-white transition-colors duration-200"
        >
          <Phone size={15} /> {CONTACT.phone}
        </a>

        <button
          className="lg:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 border-b border-[#223026]"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-lg text-neutral-300 hover:text-lime-400"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={CONTACT.phoneHref}
                className="inline-flex items-center gap-2 bg-lime-400 text-black font-semibold px-5 py-3 mt-2 w-max"
              >
                <Phone size={16} /> {CONTACT.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
